import React, { useState, useEffect, useMemo } from 'react';
import { 
  MapPin, 
  AlertCircle, 
  Loader, 
  CheckCircle2, 
  Tags,
  Building,
  X,
  Navigation,
  CornerDownRight,
  Search as SearchIcon,
  ChevronRight,
  Plus,
  HelpCircle
} from 'lucide-react';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from "../context/BusinessContext";
import { toast } from "sonner";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import PageHeader from "../components/PageHeader";
import { useNavigate } from 'react-router-dom';
import LocationDetailsPanel from '../components/location/LocationDetailsPanel';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useMediaQuery } from "../hooks/use-media-query";
import useHelpPanel from '../hooks/use-help-panel';
import HelpPanel from '../components/HelpPanel';

// Help panel content for the Locations page
const helpPanelSections = [
  {
    id: "locations-overview",
    title: "Understanding Location Data",
    content: `
      <div class="space-y-4">
        <p>Your location data helps your AI assistant provide accurate information to callers who ask about your business locations, directions, or nearby landmarks.</p>
        
        <div class="flex items-start p-3 bg-blue-50 rounded-lg">
          <div class="mr-3 mt-0.5 text-blue-500 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>
          </div>
          <div>
            <p class="text-blue-800 font-medium">Location data enhances your AI assistant's knowledge.</p>
            <p class="text-blue-600 text-sm mt-1">When a caller asks "How do I get to your store near Main Street?" your AI will know exactly which location they're referring to.</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "google-verification",
    title: "Google Verification",
    content: `
      <div class="space-y-4">
        <p>Verifying your address with Google Maps ensures your AI assistant has accurate location data to help callers find your business.</p>
        
        <h3 class="text-sm font-medium text-gray-900 mt-4 mb-2">To verify your address:</h3>
        <ol class="list-decimal list-inside space-y-2 text-gray-700">
          <li>Select a location from the list</li>
          <li>Click the "Add address" button</li>
          <li>Type your address and select the correct option from the dropdown</li>
          <li>Save your verified address</li>
        </ol>
        
        <div class="p-3 border border-emerald-100 rounded-lg bg-emerald-50 mt-2">
          <h4 class="text-sm font-medium text-emerald-800 mb-2">Benefits of verified addresses:</h4>
          <ul class="list-disc list-inside space-y-1 text-emerald-700 text-sm">
            <li>Precise directions for callers</li>
            <li>Accurate representation on maps</li>
            <li>Better contextual understanding for your AI</li>
            <li>Enhanced caller experience</li>
          </ul>
        </div>
      </div>
    `
  },
  {
    id: "alternative-names",
    title: "Alternative Names",
    content: `
      <div class="space-y-4">
        <p>Add alternative names that customers might use to refer to your location. This helps your AI assistant recognize different ways people might refer to your business.</p>
        
        <div class="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-100">
          <div>
            <h4 class="text-sm font-medium text-gray-900 mb-2">Examples of good alternative names:</h4>
            <ul class="list-disc list-inside space-y-1 text-gray-600 text-sm">
              <li>"Downtown location" (if your business is in downtown)</li>
              <li>"Main Street store" (if on Main Street)</li>
              <li>"North side branch" (if on the north side)</li>
              <li>Any nicknames locals use for your business</li>
            </ul>
          </div>
        </div>
        
        <p class="text-sm text-gray-600 mt-2">When someone calls and asks about "your downtown location," your AI will understand they're referring to this specific place.</p>
      </div>
    `
  },
  {
    id: "landmarks",
    title: "Nearby Landmarks",
    content: `
      <div class="space-y-4">
        <p>Adding landmarks near your business location helps callers get better directions and helps your AI understand location-based questions.</p>
        
        <h3 class="text-sm font-medium text-gray-900 mt-4 mb-2">Effective landmarks to add:</h3>
        <ul class="list-disc list-inside space-y-1 text-gray-700">
          <li>Well-known buildings or businesses</li>
          <li>Parks or public spaces</li>
          <li>Notable statues or monuments</li>
          <li>Shopping centers or malls</li>
        </ul>
        
        <div class="flex items-start p-3 bg-amber-50 rounded-lg mt-3">
          <div class="mr-3 mt-0.5 text-amber-500 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-building"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>
          </div>
          <div>
            <p class="text-amber-800 font-medium">How this helps callers</p>
            <p class="text-amber-600 text-sm mt-1">When someone asks "Are you near City Hall?" your AI can respond accurately if you've added City Hall as a landmark.</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "roads",
    title: "Nearby Roads",
    content: `
      <div class="space-y-4">
        <p>Adding nearby roads and intersections helps your AI assistant give better directions and understand location references.</p>
        
        <h3 class="text-sm font-medium text-gray-900 mt-4 mb-2">What to include:</h3>
        <ul class="list-disc list-inside space-y-1 text-gray-700">
          <li>Major streets your location is on or near</li>
          <li>Notable intersections (e.g., "Main St & 5th Ave")</li>
          <li>Highway exits or entry points nearby</li>
          <li>Well-known roads in your area</li>
        </ul>
        
        <div class="p-3 border border-gray-200 rounded-lg bg-gray-50 mt-2">
          <h4 class="text-sm font-medium text-gray-900 mb-2">Example road entries:</h4>
          <ul class="space-y-1 text-gray-600 text-sm">
            <li>• "Main Street & Oak Avenue intersection"</li>
            <li>• "Next to Highway 101 North exit"</li>
            <li>• "Corner of Washington Blvd and Lincoln Ave"</li>
          </ul>
        </div>
      </div>
    `
  },
  {
    id: "location-priority",
    title: "Location Priority",
    content: `
      <div class="space-y-4">
        <p>The order of your locations matters when you have multiple business locations. Your AI assistant will mention your highest-priority location first when appropriate.</p>
        
        <h3 class="text-sm font-medium text-gray-900 mt-4 mb-2">Priority considerations:</h3>
        <ul class="list-disc list-inside space-y-1 text-gray-700">
          <li>Your main or headquarters location should typically be first</li>
          <li>Locations with more customer traffic may deserve higher priority</li>
          <li>Consider which location you want callers to visit if they don't specify</li>
        </ul>
        
        <div class="flex items-start p-3 bg-purple-50 rounded-lg mt-2">
          <div class="mr-3 mt-0.5 text-purple-500 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>
          </div>
          <div>
            <p class="text-purple-800 font-medium">Why priority matters</p>
            <p class="text-purple-600 text-sm mt-1">When someone asks "What are your business hours?" without specifying a location, your AI will default to information about your highest priority location.</p>
          </div>
        </div>
      </div>
    `
  }
];

// Location item component with expandable details
const LocationItem = ({ 
  location, 
  showPriority, 
  supabase, 
  onFetchLocations,
  isExpanded,
  onToggleExpand,
  index,
  total
}) => {
  // Convert order number to integer to remove leading zeros
  const orderNumber = location.ivr_order ? parseInt(location.ivr_order, 10) : '';
  
  // Get data counts
  const alternativeNamesCount = location.alternative_names?.length || 0;
  const landmarksCount = location.landmarks?.length || 0;
  const roadsCount = location.roads?.length || 0;
  const totalExtras = alternativeNamesCount + landmarksCount + roadsCount;
  
  // Check if location has Google verification
  const hasGoogleVerification = !!(location.google_places_id && location.google_address);

  // Animation variants for the card
  const cardVariants = {
    initial: { opacity: 0, scale: 0.98 },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 20,
        delay: index * 0.05  // Staggered entrance
      }
    },
    hover: { 
      scale: 1.01,
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.05)",
      transition: { 
        type: "tween", 
        duration: 0.2
      }
    },
    exit: {
      opacity: 0,
      scale: 0.96,
      transition: { duration: 0.2 }
    }
  };

  // Refined animation for the content panel
  const contentVariants = {
    collapsed: { 
      opacity: 0,
      height: 0,
      transition: { 
        height: { duration: 0.3, ease: "easeInOut" },
        opacity: { duration: 0.2 }
      }
    },
    expanded: { 
      opacity: 1,
      height: "auto",
      transition: { 
        height: { duration: 0.3, ease: "easeInOut" },
        opacity: { duration: 0.15, delay: 0.15 }
      }
    }
  };
  
  return (
    <motion.div 
      className="relative"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={cardVariants}
    >
      {showPriority && total > 1 && (
        <div className="absolute sm:left-[-40px] left-[-28px] top-1/2 transform -translate-y-1/2 flex flex-col items-center">
          <motion.div 
            className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center bg-black text-white text-xs font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 + index * 0.05 }}
          >
            {orderNumber}
          </motion.div>
          {index < total - 1 && (
            <motion.div 
              className="h-12 sm:h-16 w-px bg-gray-200 mt-2"
              initial={{ height: 0 }}
              animate={{ height: 12 }}
              transition={{ delay: 0.3 + index * 0.05 }}
            />
          )}
        </div>
      )}
      
      <motion.div 
        className={`
          bg-white border-0 
          ${isExpanded ? 'rounded-2xl shadow-md' : 'rounded-xl shadow-sm'}
          overflow-hidden
        `}
        whileHover="hover"
        variants={cardVariants}
      >
        <div 
          className={`
            p-4 sm:p-5 cursor-pointer flex items-center
            ${isExpanded ? 'border-b border-gray-100' : ''}
          `}
          onClick={() => onToggleExpand(location.id)}
        >
          <div className="flex-1">
            <div className="flex items-center">
              <h3 className={`font-medium ${isExpanded ? 'text-base sm:text-lg' : 'text-sm sm:text-base'} tracking-tight`}>
                {location.name}
              </h3>
              
              {hasGoogleVerification && (
                <motion.span 
                  className="ml-2 bg-emerald-50 text-emerald-700 text-xs rounded-full py-0.5 px-1.5 sm:px-2 flex items-center"
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <CheckCircle2 size={10} className="mr-0.5 sm:mr-1 sm:w-3 sm:h-3" />
                  <span className="text-[10px] sm:text-xs">Verified</span>
                </motion.span>
              )}
            </div>
            
            {totalExtras > 0 && (
              <div className="flex mt-1.5 sm:mt-2 space-x-2 sm:space-x-3">
                {alternativeNamesCount > 0 && (
                  <motion.span 
                    className="flex items-center text-[10px] sm:text-xs text-gray-500"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <Tags size={10} className="mr-0.5 sm:mr-1 text-gray-400 sm:w-3 sm:h-3" />
                    {alternativeNamesCount} name{alternativeNamesCount !== 1 ? 's' : ''}
                  </motion.span>
                )}
                
                {landmarksCount > 0 && (
                  <motion.span 
                    className="flex items-center text-[10px] sm:text-xs text-gray-500"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15 + index * 0.05 }}
                  >
                    <Building size={10} className="mr-0.5 sm:mr-1 text-gray-400 sm:w-3 sm:h-3" />
                    {landmarksCount} landmark{landmarksCount !== 1 ? 's' : ''}
                  </motion.span>
                )}
                
                {roadsCount > 0 && (
                  <motion.span 
                    className="flex items-center text-[10px] sm:text-xs text-gray-500"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                  >
                    <Navigation size={10} className="mr-0.5 sm:mr-1 text-gray-400 sm:w-3 sm:h-3" />
                    {roadsCount} road{roadsCount !== 1 ? 's' : ''}
                  </motion.span>
                )}
              </div>
            )}
          </div>
          
          <motion.div
            animate={{
              rotate: isExpanded ? 90 : 0,
              opacity: isExpanded ? 0.8 : 0.5
            }}
            transition={{ duration: 0.3, type: "spring", stiffness: 400 }}
            className="text-gray-400"
          >
            <ChevronRight size={16} className="sm:w-5 sm:h-5" />
          </motion.div>
        </div>
        
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div 
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={contentVariants}
              className="overflow-hidden"
            >
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="px-4 sm:px-5 pb-4 sm:pb-5 pt-2 sm:pt-3"
              >
                <LocationDetailsPanel
                  location={location}
                  expanded={isExpanded}
                  onToggleExpand={() => onToggleExpand(location.id)}
                  supabase={supabase}
                  onUpdate={onFetchLocations}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// Custom search input with animations
const SearchInput = ({ value, onChange, onClear }) => {
  const [isFocused, setIsFocused] = useState(false);
  
  return (
    <motion.div 
      className={`
        relative rounded-full bg-white border
        ${isFocused ? 'border-black shadow-sm' : 'border-gray-200'}
        transition-all duration-200 overflow-hidden
      `}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.3 }}
    >
      <div className="flex items-center w-full">
        <motion.div 
          className="pl-3 sm:pl-4 text-gray-400"
          animate={{ opacity: isFocused || value ? 0.9 : 0.5 }}
        >
          <SearchIcon size={16} />
        </motion.div>
        <Input
          type="text"
          placeholder="Search locations..."
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="border-0 shadow-none focus-visible:ring-0 bg-transparent h-9 sm:h-10 text-sm"
        />
        {value && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={onClear}
            className="pr-3 sm:pr-4 text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// Empty state component when no locations exist
const NoLocationsState = ({ navigate }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.3 }}
    className="min-h-[300px] flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 p-6 sm:p-8"
  >
    <MapPin size={24} className="text-gray-300 mb-4" />
    <h3 className="text-gray-800 font-medium text-lg mb-2">No locations found</h3>
    <p className="text-gray-600 text-center font-light text-sm mb-6 max-w-md">
      You haven't added any locations yet. Locations help your AI assistant provide accurate information about your business.
    </p>
    <Button 
      onClick={() => navigate('/account/locations')} 
      className="flex items-center"
    >
      <Plus size={16} className="mr-2" />
      Add Your First Location
    </Button>
  </motion.div>
);

// Loading animation
const LoadingAnimation = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6 text-center"
    >
      <motion.div
        animate={{ 
          rotate: 360,
          opacity: [0.3, 0.7, 0.3], 
          scale: [0.98, 1.02, 0.98]
        }}
        transition={{ 
          rotate: { duration: 2, ease: "linear", repeat: Infinity }, 
          opacity: { duration: 2, repeat: Infinity, ease: "easeInOut" },
          scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
        }}
        className="mx-auto w-12 h-12 flex items-center justify-center"
      >
        <MapPin className="w-10 h-10 text-black opacity-80" />
      </motion.div>
      <p className="text-sm text-gray-600 tracking-wide font-light">Loading your locations...</p>
    </motion.div>
  </div>
);

const LocationPriority = () => {
  // Set document title for browser tab
  useEffect(() => {
    document.title = "Locations";
  }, []);

  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const navigate = useNavigate();
  
  // Help panel hook
  const helpPanel = useHelpPanel({
    title: "Understanding Location Settings",
    sections: helpPanelSections,
    position: "right",
    size: "large"
  });
  
  // State for locations and UI
  const [locations, setLocations] = useState([]);
  const [expandedLocationIds, setExpandedLocationIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.2
      }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  const containerVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.33, 1, 0.68, 1]
      }
    }
  };
  
  // Toggle expanded state for a location
  const toggleLocationExpand = (locationId) => {
    setExpandedLocationIds(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };
  
  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);
  
  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      // Fetch locations from vapi_location_settings with all required fields
      const { data, error } = await supabase
        .from('vapi_location_settings')
        .select('id, name, ivr_order, alternative_names, landmarks, roads, google_address, google_places_id')
        .eq('account_id', user.id)
        .order('ivr_order', { ascending: true, nullsLast: true });
        
      if (error) throw error;
      
      // Sort locations: first by ivr_order if present, then by name
      const sortedLocations = [...data].sort((a, b) => {
        if (a.ivr_order && b.ivr_order) {
          return a.ivr_order.localeCompare(b.ivr_order);
        }
        if (a.ivr_order) return -1;
        if (b.ivr_order) return 1;
        return a.name.localeCompare(b.name);
      });
      
      setLocations(sortedLocations);
      
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter locations based on search query
  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locations;
    
    const query = searchQuery.toLowerCase();
    return locations.filter(location => {
      // Search in name
      if (location.name.toLowerCase().includes(query)) return true;
      
      // Search in alternative names
      if (location.alternative_names && location.alternative_names.some(name => 
        name.toLowerCase().includes(query))) return true;
      
      // Search in landmarks
      if (location.landmarks && location.landmarks.some(landmark => 
        landmark.toLowerCase().includes(query))) return true;
      
      // Search in roads
      if (location.roads && location.roads.some(road => 
        road.toLowerCase().includes(query))) return true;
      
      // Search in address
      if (location.google_address && location.google_address.toLowerCase().includes(query)) return true;
      
      return false;
    });
  }, [locations, searchQuery]);
  
  // Add this line to detect mobile screens
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  if (loading) {
    return <LoadingAnimation />;
  }
  
  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <PageHeader
        title="Locations"
        description="Manage your business locations and their details"
        icon={MapPin}
        searchQuery={isMobile ? searchQuery : undefined}
        onSearchChange={isMobile ? setSearchQuery : undefined}
        className="max-w-5xl mx-auto"
      >
        <button
          onClick={() => helpPanel.open()}
          className="flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1 shadow-sm transition-colors"
          aria-label="Help"
        >
          <HelpCircle className="h-5 w-5 text-gray-500" />
        </button>
      </PageHeader>
      
      <motion.div 
        className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-16 sm:pb-20 md:pb-24"
        variants={containerVariants}
      >
        <div className="md:flex md:space-x-8">
          <div className="md:w-64 mb-6 md:mb-0 hidden md:block">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
            />
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="mt-6 md:mt-8 space-y-3 md:space-y-4"
            >
              <h3 className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">
                {locations.length} Location{locations.length !== 1 ? 's' : ''}
              </h3>
              
              <div className="text-xs sm:text-sm text-gray-500">
                {locations.length > 1 ? (
                  <p>Your locations appear here, organized by importance. Select a location to view or edit its details.</p>
                ) : locations.length === 1 ? (
                  <p>Your location is shown below. Select it to view or edit its details.</p>
                ) : (
                  <p>Add your first location to help your AI assistant provide accurate information.</p>
                )}
              </div>
              
              {locations.length === 0 && (
                <Button 
                  onClick={() => navigate('/account/locations')} 
                  variant="outline"
                  className="mt-2 w-full"
                  size="sm"
                >
                  <Plus size={14} className="mr-1.5" />
                  Add Location
                </Button>
              )}
            </motion.div>
          </div>

          <motion.div 
            className="flex-1 relative"
            variants={containerVariants}
          >
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-100 rounded-xl flex items-center text-red-600 text-xs sm:text-sm"
                >
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Mobile-only metadata */}
            <div className="md:hidden mb-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="space-y-3"
              >
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {locations.length} Location{locations.length !== 1 ? 's' : ''}
                </h3>
                
                <div className="text-xs text-gray-500">
                  {locations.length > 1 ? (
                    <p>Your locations appear here, organized by importance. Select a location to view or edit its details.</p>
                  ) : locations.length === 1 ? (
                    <p>Your location is shown below. Select it to view or edit its details.</p>
                  ) : (
                    <p>Add your first location to help your AI assistant provide accurate information.</p>
                  )}
                </div>
                
                {locations.length === 0 && (
                  <Button 
                    onClick={() => navigate('/account/locations')} 
                    variant="outline"
                    className="mt-2 w-full"
                    size="sm"
                  >
                    <Plus size={14} className="mr-1.5" />
                    Add Location
                  </Button>
                )}
              </motion.div>
            </div>

            <LayoutGroup>
              <AnimatePresence>
                {filteredLocations.length === 0 ? (
                  locations.length === 0 ? (
                    <NoLocationsState navigate={navigate} />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                      className="min-h-[200px] sm:min-h-[300px] flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 p-6 sm:p-8"
                    >
                      <SearchIcon size={20} className="text-gray-300 mb-3 sm:mb-4" />
                      <p className="text-gray-600 text-center font-light text-sm">No locations found matching your search criteria.</p>
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          onClick={() => setSearchQuery('')}
                          className="mt-3 sm:mt-4 text-xs sm:text-sm"
                        >
                          Clear Search
                        </Button>
                      )}
                    </motion.div>
                  )
                ) : (
                  <motion.div 
                    className="pl-4 sm:pl-10 space-y-3 sm:space-y-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ staggerChildren: 0.08 }}
                  >
                    {filteredLocations.map((location, index) => (
                      <LocationItem
                        key={location.id}
                        location={location}
                        showPriority={!searchQuery}
                        supabase={supabase}
                        onFetchLocations={fetchLocations}
                        isExpanded={expandedLocationIds.includes(location.id)}
                        onToggleExpand={toggleLocationExpand}
                        index={index}
                        total={filteredLocations.length}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </LayoutGroup>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Help Panel */}
      <HelpPanel
        isOpen={helpPanel.isOpen}
        onClose={helpPanel.close}
        title={helpPanel.config.title}
        content={helpPanel.config.content}
        sections={helpPanel.config.sections}
        position={helpPanel.config.position}
        size={helpPanel.config.size}
      />
    </motion.div>
  );
};

export default LocationPriority;